/**
 * Home Page
 * Main calendar view for managing shifts, events, and tasks.
 * Two-panel layout: TaskSidebar on left, CalendarView on right.
 * Wraps both in a shared DndContext so sidebar tasks can be dropped onto any calendar view.
 */

import { useState, useCallback, useEffect } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragMoveEvent,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  pointerWithin,
} from '@dnd-kit/core';
import type { Task } from '@signalvote/shared';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { mapTaskListItemToTask, getAssignmentDirection, getAssigneeInitials, getTaskPalette } from '@/lib/taskAssignmentUtils';
import { useDialogState } from '@/hooks/useDialogState';
import { useUndoDelete } from '@/hooks/useUndoDelete';
import { useTasks, useUpdateTask } from '@/hooks/queries';
import { useTaskStore, useCalendarStore } from '@/stores';
import { HOUR_HEIGHT, SNAP_MINUTES, snapToTimeGrid, minutesToTimeString, formatTime12Hour } from '@/lib/calendarUtils';
import { TaskSidebar } from '@/components/tasks';
import { CalendarView, TaskDetailsDialog } from '@/components/calendar';
import { FloatingHelp, HELP_TOPICS } from '@/components/help';
import { WorkspaceWelcomeTour } from '@/components/onboarding';
import { VoteGoalCard } from '@/components/race-picture';
import { isVoteGoalOn } from '@/config/customerConfig';

// ============================================================================
// Route Definition
// ============================================================================

export const Route = createFileRoute('/app/home')({
  component: HomePage,
});

// ============================================================================
// Types
// ============================================================================

interface TaskDialogData {
  task: Task;
}

// ============================================================================
// TaskDragPreview - visual preview shown while dragging a task
// ============================================================================

interface TaskDragPreviewProps {
  task: Task;
  currentUserId?: string;
  /** Snapped time label shown while hovering over a time-grid column (e.g. "2:30 PM") */
  snapTimeLabel?: string | null;
}

function TaskDragPreview({ task, currentUserId, snapTimeLabel }: TaskDragPreviewProps) {
  const direction = getAssignmentDirection(task, currentUserId);
  const palette = getTaskPalette(false, direction);
  const initials = getAssigneeInitials(task, direction);

  return (
    <div className="flex flex-col items-center gap-1">
      {/* Snap-time chip - floats above the drag preview */}
      {snapTimeLabel && (
        <div className="px-2.5 py-1 rounded-md bg-foreground text-background text-xs font-semibold shadow-lg whitespace-nowrap">
          {snapTimeLabel}
        </div>
      )}
      <div className={`w-[160px] p-2 rounded-md border-l-4 shadow-lg opacity-90 ${palette.block}`}>
        <p className={`text-xs font-semibold line-clamp-2 ${palette.title}`}>{task.title}</p>
        {initials && (
          <span
            className={cn(
              'text-[0.55rem] px-1 py-0.5 rounded font-medium inline-block mt-0.5',
              direction === 'inbound'
                ? 'border border-blue-300 text-blue-700 bg-blue-50'
                : 'border border-violet-300 text-violet-700 bg-violet-50'
            )}
          >
            {direction === 'inbound' ? '\u2190' : '\u2192'} {initials}
          </span>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

function HomePage() {
  // Dialog state for task details when clicking from sidebar
  const taskDetailsDialog = useDialogState<TaskDialogData>();

  // Undo delete hook for task deletion from sidebar
  const { requestDelete, undoDelete, isSnackbarOpen, pendingTaskTitle } = useUndoDelete();

  // Sync API tasks into Zustand store so calendar views display real tasks
  const { data: taskResponse } = useTasks({ myTasks: true, includeCreatedByMe: true, pageSize: 100 });
  const mergeApiTasks = useTaskStore((s) => s.mergeApiTasks);

  useEffect(() => {
    if (taskResponse?.items) {
      mergeApiTasks(taskResponse.items.map(mapTaskListItemToTask));
    }
  }, [taskResponse, mergeApiTasks]);

  // Auth for drag overlay direction styling
  const { user } = useAuth();

  // DnD state
  const [activeTask, setActiveTask] = useState<Task | null>(null);
  const [dragTimeLabel, setDragTimeLabel] = useState<string | null>(null);
  const { mutate: updateTaskMutate } = useUpdateTask();
  const currentView = useCalendarStore((s) => s.currentView);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const task = event.active.data.current?.task as Task | undefined;
    if (task) setActiveTask(task);
  }, []);

  const handleDragMove = useCallback((event: DragMoveEvent) => {
    const { active, over } = event;
    if (!over) {
      setDragTimeLabel(null);
      return;
    }

    const isTimeGrid = over.data.current?.type === 'time-grid';
    if (!isTimeGrid) {
      setDragTimeLabel(null);
      return;
    }

    const translated = active.rect.current.translated;
    const overRect = over.rect;
    if (translated && overRect) {
      const yOffset = translated.top - overRect.top;
      const rawMinutes = (yOffset / HOUR_HEIGHT) * 60;
      const snappedMinutes = Math.round(rawMinutes / SNAP_MINUTES) * SNAP_MINUTES;
      const timeStr = minutesToTimeString(snappedMinutes);
      setDragTimeLabel(formatTime12Hour(timeStr));
    }
  }, []);

  /**
   * Persist a drag update with optimistic UI.
   * Updates Zustand immediately so the calendar reflects the change,
   * then fires the API mutation to persist. Reverts on error.
   */
  const persistDragUpdate = useCallback(
    (task: Task, updates: Partial<Task>) => {
      const store = useTaskStore.getState();

      // Snapshot for rollback
      const existing = store.getTaskById(task.id);
      const previousTask = existing ? { ...existing } : null;

      // Optimistic update: apply to Zustand immediately
      if (existing) {
        store.updateTask(task.id, updates);
      } else {
        store.addTask({ ...task, ...updates });
      }

      // Persist to API
      updateTaskMutate(
        { id: task.id, data: updates },
        {
          onError: () => {
            // Revert optimistic update on failure
            if (previousTask) {
              useTaskStore.getState().updateTask(task.id, previousTask);
            } else {
              useTaskStore.getState().deleteTask(task.id);
            }
          },
        }
      );
    },
    [updateTaskMutate]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveTask(null);
      setDragTimeLabel(null);

      if (!over) return;

      const task = active.data.current?.task as Task | undefined;
      const dropType = over.data.current?.type as string | undefined;
      const newDate = over.data.current?.date as string | undefined;

      if (!task || !newDate) return;

      // Month view or fallback: set date only, preserve existing time
      if (dropType !== 'time-grid') {
        if (task.dueDate !== newDate) {
          persistDragUpdate(task, { dueDate: newDate });
        }
        return;
      }

      // Week/Day view drop: calculate time from pixel offset
      const translated = active.rect.current.translated;
      const overRect = over.rect;

      let newTime: string | undefined;

      if (translated && overRect) {
        const yOffset = translated.top - overRect.top;
        const rawMinutes = (yOffset / HOUR_HEIGHT) * 60;
        const snappedMinutes = Math.round(rawMinutes / SNAP_MINUTES) * SNAP_MINUTES;
        newTime = minutesToTimeString(snappedMinutes);
      }

      const dateChanged = task.dueDate !== newDate;
      const timeChanged = newTime && task.dueTime !== newTime;

      if (dateChanged || timeChanged) {
        const updates: Partial<Task> = { dueDate: newDate };
        if (newTime) updates.dueTime = newTime;
        persistDragUpdate(task, updates);
      }
    },
    [persistDragUpdate]
  );

  // Handle task click from sidebar to open details dialog
  const handleTaskClick = (task: Task) => {
    taskDetailsDialog.open({ task });
  };

  // Only apply snap modifier for time-grid views (week/day), not month
  const modifiers = currentView === 'month' ? [] : [snapToTimeGrid];

  return (
    <DndContext
      sensors={sensors}
      modifiers={modifiers}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
    >
      {/* Account for main padding (p-6 = 24px * 2); -m-6 offsets parent padding */}
      <div className="flex h-[calc(100vh-48px)] -m-6 overflow-hidden">
        {/* Task Sidebar */}
        <TaskSidebar onTaskClick={handleTaskClick} draggable />

        {/* Calendar Area */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {isVoteGoalOn() && (
            <div className="shrink-0 px-4 pt-3 empty:hidden">
              <VoteGoalCard />
            </div>
          )}
          <CalendarView />
        </div>

        {/* Task Details Dialog - opened from sidebar clicks */}
        <TaskDetailsDialog
          open={taskDetailsDialog.isOpen}
          onClose={taskDetailsDialog.close}
          task={taskDetailsDialog.data?.task ?? null}
          onDelete={requestDelete}
        />

        {/* Undo Delete snackbar */}
        {isSnackbarOpen && (
          <div className="fixed bottom-4 left-4 z-50 flex items-center gap-4 rounded-md bg-foreground px-4 py-3 text-background shadow-lg">
            <span>Task "{pendingTaskTitle}" deleted</span>
            <button
              onClick={undoDelete}
              className="text-sm font-medium uppercase text-secondary hover:underline"
            >
              Undo
            </button>
          </div>
        )}
      </div>

      {/* Drag Overlay - shows preview of task being dragged */}
      <DragOverlay modifiers={[snapToTimeGrid]} dropAnimation={null}>
        {activeTask && <TaskDragPreview task={activeTask} currentUserId={user?.id} snapTimeLabel={dragTimeLabel} />}
      </DragOverlay>

      <FloatingHelp topic={HELP_TOPICS.home} />
      <WorkspaceWelcomeTour />
    </DndContext>
  );
}

export default HomePage;
